import cn from 'classnames';
import type { DependencyList, DetailedHTMLProps, HTMLAttributes} from 'react';
import { useCallback, useEffect, useRef } from 'react';
import useBooleanState from '../utils/useBooleanState';
import Icon from './Icon';
import * as style from './ScrollableDiv.module.css';

export default function ScrollableDiv({ children, className, ...props }: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
    const { isPreviousButtonVisible, isNextButtonVisible, scrollLeft, scrollRight, listRef } = useScrollButtons([children]);

    return (
        <div className={cn(style.scrollableDiv, className)} {...props}>
            {isPreviousButtonVisible && (
                <button className={style.previousButton} aria-hidden onClick={scrollLeft}>
                    <Icon className="icon-chevron-left white"></Icon>
                </button>
            )}
            <div className={style.overflow} ref={listRef}>
                {children}
            </div>
            {isNextButtonVisible && (
                <button className={style.nextButton} aria-hidden onClick={scrollRight}>
                    <Icon className="icon-chevron-right white"></Icon>
                </button>
            )}
        </div>
    );
}

export function useScrollButtons(deps: DependencyList) {
    const listRef = useRef<HTMLDivElement>(null);
    const [isNextButtonVisible, setIsNextButtonVisible] = useBooleanState(false);
    const [isPreviousButtonVisible, setIsPreviousButtonVisible] = useBooleanState(false);

    const scrollLeft = useCallback(() => {
        listRef.current?.scrollBy({
            behavior: 'smooth',
            left: -listRef.current.clientWidth / 1.1,
        });
    }, []);

    const scrollRight = useCallback(() => {
        listRef.current?.scrollBy({
            behavior: 'smooth',
            left: listRef.current.clientWidth / 1.1,
        });
    }, []);

    useEffect(() => {
        if (!listRef.current) return;
        const elm = listRef.current;

        const updateButtonVisibility = () => {
            const maxScroll = elm.scrollWidth - elm.clientWidth;
            setIsNextButtonVisible(elm.scrollLeft < maxScroll - 1);
            setIsPreviousButtonVisible(elm.scrollLeft > 1);
        };
        elm.addEventListener('scrollend', updateButtonVisibility);

        updateButtonVisibility();

        return () => elm.removeEventListener('scrollend', updateButtonVisibility);
    }, [listRef.current, ...deps]);

    return { isPreviousButtonVisible, isNextButtonVisible, scrollLeft, scrollRight, listRef };
}
